import { type FunctionComponent, type HTMLAttributes, type MouseEvent, type ReactElement, type ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAudioTrigger } from '../../controllers/audioController/hooks';
import { useMedia } from '../../hooks/useMedia';
import { useModifiers } from '../../styles/hooks/useModifiers';
import { IconButton, IconButtonSize, Icons } from '../controls/iconButton/IconButton';
import { PlayingNowIcon } from '../icons/animated/playingNow/PlayingNowIcon';
import { Icon } from '../icons/icon';

import { BACK_TO_GAME_SPECIAL_SIZE_RULE } from './constants';
import { VideoMirror } from './videoMirror/VideoMirror';

import styles from './BackToGame.module.css';

export interface BackToGameProps {
  /** Action label */
  actionLabel?: ReactNode;
  /** Whether the component is temporarily hidden */
  isHidden?: boolean;
  /** Whether the component is open and "alive" */
  isOpen: boolean;
  /** Game name */
  name?: ReactNode;
  /** On click handler */
  onClick?: HTMLAttributes<HTMLDivElement>['onClick'];
  /** On close handler */
  onClose?: () => void;
  /** Placeholder image src */
  poster?: string | ReactElement;
  /** Traffic light ReactNode */
  trafficLight?: ReactElement;
  /** A video element to get video data from */
  videoElement?: HTMLVideoElement | null;
  /** Label to be shown in besides an animated indicator */
  nowPlayingLabel?: ReactNode;
  /** A node of secondary actions, for example a mute icon button */
  secondaryActions?: ReactNode;
  /** Enable the updated UI (V2) for the "Back to Game" feature. */
}

export const BackToGame: FunctionComponent<BackToGameProps> = ({
  actionLabel,
  isHidden,
  isOpen,
  name,
  onClick,
  onClose,
  poster,
  trafficLight,
  videoElement,
  nowPlayingLabel,
  secondaryActions,
}) => {
  const audioTrigger = useAudioTrigger();
  const posterRef = useRef<HTMLImageElement>(null);
  const backToGameRef = useRef<HTMLDivElement>(null);
  const [localIsOpen, setLocalIsOpen] = useState<boolean>(isOpen);
  const isBackToGameOpen = isOpen && localIsOpen && !isHidden;
  const wrapperMods = useModifiers(
    styles,
    'backToGame__wrapper',
    { noShadow: !isBackToGameOpen },
    { withBaseClass: true }
  );
  const classNameWithMods = useModifiers(styles, 'backToGame', { isOpen: isBackToGameOpen }, { withBaseClass: true });
  const isSmall = useMedia(BACK_TO_GAME_SPECIAL_SIZE_RULE);

  useEffect(() => {
    if (isOpen) {
      setLocalIsOpen(true);
      return;
    }
    const timeout = setTimeout(() => {
      setLocalIsOpen(false);
    }, 300);
    return (): void => {
      clearTimeout(timeout);
    };
  }, [isOpen, setLocalIsOpen]);

  const handleCloseClick = useCallback(
    function handleCloseFunc(event?: MouseEvent<HTMLButtonElement>): void {
      if (event) {
        event.stopPropagation();
      }
      if (onClose) {
        audioTrigger.play({ name: 'menuClick' });
        onClose();
      }
    },
    [audioTrigger, onClose]
  );

  const handleClick = useCallback(
    function handleClickFunc(event: MouseEvent<HTMLDivElement>): void {
      if (localIsOpen && onClick) {
        audioTrigger.play({ name: 'menuClick' });
        onClick(event);
      }
    },
    [audioTrigger, localIsOpen, onClick]
  );

  if (!isOpen && !localIsOpen) return null;

  return (
    <div className={wrapperMods} data-testid="back-to-game">
      <div className={classNameWithMods} onClick={handleClick} ref={backToGameRef}>
        {trafficLight ? <div className={styles.backToGame__trafficLight}>{trafficLight}</div> : null}
        <div className={styles.backToGame__container}>
          <div className={styles.backToGame__videoContainer}>
            {typeof poster === 'string' ? (
              <img src={poster} className={styles.backToGame__poster} alt="" ref={posterRef} />
            ) : (
              poster
            )}
            {videoElement ? <VideoMirror videoElement={videoElement} /> : null}
          </div>
          <div className={styles.backToGame__content}>
            <div className={styles.backToGame__name}>{name}</div>
            {nowPlayingLabel ? (
              <div className={styles.backToGame__nowPlaying}>
                <div className={styles.backToGame__nowPlayingIcon}>
                  <PlayingNowIcon />
                </div>
                <div className={styles.backToGame__nowPlayingLabel}>{nowPlayingLabel}</div>
              </div>
            ) : null}
            <div className={styles.backToGame__action}>
              {actionLabel}
              <Icon icon={Icons.ChevronRight} />
            </div>
          </div>
          {onClose || secondaryActions ? (
            <div className={styles.backToGame__actions}>
              {secondaryActions ?? null}
              {onClose ? (
                <IconButton
                  ghost
                  icon={Icons.Close}
                  onClick={handleCloseClick}
                  size={isSmall ? IconButtonSize.SMALL : IconButtonSize.TINY}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
